import jsPDF from "jspdf"
import removeTimezone from "./removeTimezone"

// ─── Types ────────────────────────────────────────────────────────────────────

interface StatementPayment {
    date: string
    amount: number
    amount_tickets: number
    remarks?: string | null
    payment_method?: string | null
}

interface StatementMovement {
    kind: 'traspaso' | 'general_payment'
    date: string
    balance: 'positive' | 'negative'
    // traspaso fields
    passenger_name?: string | null
    passenger_nationality?: string | null
    tour_name?: string | null
    traspass_amount?: number
    tickets_amount?: number
    revision_status?: string | null
    revision_ticket_status?: string | null
    revision_notes?: string | null
    revision_chat_source?: string | null
    payments?: StatementPayment[]
    // general payment fields
    amount?: number
    amount_tickets?: number
    remarks?: string | null
    payment_method?: string | null
}

interface StatementSummary {
    reservations_balance: number
    retraspaso_balance: number
    payments_balance: number
    total: number
}

export interface AccountStatement {
    counterpart_agency: { id: string; name: string; email?: string; display_name?: string | null }
    from: string | null
    to: string | null
    summary: StatementSummary | null
    // Saldo acumulado ANTES de `from` (null cuando el estado es histórico completo).
    // Signo igual que summary.total: > 0 la agencia le debe a Molantur.
    opening_balance?: number | null
    positive: StatementMovement[]
    negative: StatementMovement[]
}

// ─── Constantes de Molantur (emisor del estado de cuenta) ────────────────────

const ISSUER = {
    name: 'MOLANTUR',
    subtitle: 'ATACAMA TOURS',
    short: 'Molantur',
    contact_email: 'contacto@molantur.com',
    // Mismos datos que manda el bot de WhatsApp para transferencias en Chile.
    bank: {
        holder: 'Ckuru Atacama Spa',
        rut: '77.935.742-2',
        bank: 'Banco BCI',
        account: 'Cuenta corriente N° 32832001',
        email: 'molantur@gmail.com'
    }
}

// ─── Labels ──────────────────────────────────────────────────────────────────

const TICKET_STATUS_LABELS: Record<string, string> = {
    included_in_account: 'Incluida en cuenta',
    paid_cash: 'Pagada en efectivo',
    paid_transfer: 'Pagada por transferencia',
    paid_at_agency: 'Pagada en agencia',
    passenger_pays: 'Pasajero lleva efectivo',
    pending_payment: 'Pendiente de pago',
    qr_sent: 'QR enviado por chat',
    no_entrance: 'Sin entrada (tour libre)',
    not_determined: 'No determinado'
}

type RGB = [number, number, number]

// Semántica de color del estado de la entrada:
//   verde  = no genera deuda en esta cuenta
//   ámbar  = incluida en cuenta (SÍ suma al saldo)
//   rojo   = pendiente de pago
//   gris   = no determinado
const TICKET_STATUS_COLORS: Record<string, { fg: RGB; bg: RGB }> = {
    included_in_account: { fg: [140, 84, 10], bg: [253, 238, 208] },
    pending_payment: { fg: [150, 30, 30], bg: [252, 229, 229] },
    not_determined: { fg: [110, 108, 102], bg: [238, 237, 232] }
}
const TICKET_STATUS_OK: { fg: RGB; bg: RGB } = { fg: [43, 94, 24], bg: [226, 242, 222] }

// Badge de revisión: solo se muestra el estado positivo. Los estados internos (pendiente,
// error, sin respaldo de chat) no se le muestran a la agencia.
const REVIEWED_LABEL = 'Revisado'
const REVIEWED_COLORS: { fg: RGB; bg: RGB } = { fg: [12, 68, 124], bg: [230, 241, 251] }

const C = {
    navy: [4, 44, 83] as RGB,
    ink: [24, 24, 24] as RGB,
    body: [70, 70, 70] as RGB,
    muted: [128, 126, 120] as RGB,
    faint: [170, 168, 160] as RGB,
    rule: [222, 220, 213] as RGB,
    band: [246, 246, 243] as RGB,
    boxBg: [251, 250, 247] as RGB,
    debtRed: [150, 30, 30] as RGB,
    creditGreen: [39, 109, 10] as RGB
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// CLP sin decimales: $21.000
function money(value?: number | null): string {
    const n = Math.round(Number(value) || 0)
    const sign = n < 0 ? '−' : ''
    const digits = Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    return `${sign}$${digits}`
}

// Igual que money pero sin el símbolo (para las líneas de conciliación).
function plain(value?: number | null): string {
    return money(value).replace('$', '')
}

function fmtDate(value?: string | null): string {
    if (!value) return ''
    try {
        return removeTimezone(value.toString())
    } catch {
        return ''
    }
}

function dateKey(value?: string | null): string {
    if (!value) return ''
    const s = value.toString()
    const t = s.indexOf('T')
    return t === -1 ? s : s.slice(0, t)
}

function fmtToday(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
}

// ─── Modelo de presentación ──────────────────────────────────────────────────

interface PaxRow {
    name: string
    reviewed: boolean          // revisión verificada contra el chat → badge "Revisado"
    ticket_status: string | null
    traspaso: number
    entradas: number
    abonado: number
    pendiente: number
}

interface SalidaGroup {
    date: string        // yyyy-mm-dd
    dateLabel: string
    tour: string
    pax: PaxRow[]
    traspaso: number
    entradas: number
    abonado: number
    pendiente: number
}

interface GeneralPaymentRow {
    dateLabel: string
    amount: number
    method: string | null
    remarks: string | null
}

interface SideModel {
    groups: SalidaGroup[]          // salidas con saldo pendiente
    settled: SalidaGroup[]         // salidas con pendiente = 0
    generalPayments: GeneralPaymentRow[]
    totals: { traspasos: number; entradas: number; abonado: number; pendiente: number }
}

function buildSide(movements: StatementMovement[]): SideModel {
    const map = new Map<string, SalidaGroup>()
    const generalPayments: GeneralPaymentRow[] = []
    const totals = { traspasos: 0, entradas: 0, abonado: 0, pendiente: 0 }

    movements.forEach(m => {
        if (m.kind === 'general_payment') {
            const amount = (Number(m.amount) || 0) + (Number(m.amount_tickets) || 0)
            generalPayments.push({
                dateLabel: fmtDate(m.date),
                amount,
                method: m.payment_method || null,
                remarks: m.remarks || null
            })
            totals.abonado += amount
            return
        }
        const traspaso = Number(m.traspass_amount) || 0
        const entradas = Number(m.tickets_amount) || 0
        const abonado = (m.payments || []).reduce((acc, p) => acc + (Number(p.amount) || 0) + (Number(p.amount_tickets) || 0), 0)
        const pendiente = traspaso + entradas - abonado
        const key = `${dateKey(m.date)}|${m.tour_name || ''}`
        let group = map.get(key)
        if (!group) {
            group = {
                date: dateKey(m.date),
                dateLabel: fmtDate(m.date),
                tour: m.tour_name || 'Tour',
                pax: [],
                traspaso: 0, entradas: 0, abonado: 0, pendiente: 0
            }
            map.set(key, group)
        }
        group.pax.push({
            name: m.passenger_name || 'Pasajero',
            reviewed: m.revision_status === 'verified',
            ticket_status: m.revision_ticket_status || null,
            traspaso, entradas, abonado, pendiente
        })
        group.traspaso += traspaso
        group.entradas += entradas
        group.abonado += abonado
        group.pendiente += pendiente
        totals.traspasos += traspaso
        totals.entradas += entradas
        totals.abonado += abonado
    })

    // Una salida está saldada solo si TODOS sus pasajeros están en $0. Si hay mezcla,
    // la salida completa va al cuerpo principal (los pax saldados se ven en gris ahí).
    const all = Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date) || a.tour.localeCompare(b.tour))
    const groups = all.filter(g => g.pax.some(p => p.pendiente !== 0))
    const settled = all.filter(g => g.pax.every(p => p.pendiente === 0))
    totals.pendiente = totals.traspasos + totals.entradas - totals.abonado

    return { groups, settled, generalPayments, totals }
}

// ─── Motor de dibujo ─────────────────────────────────────────────────────────

const PAGE_MARGIN = 14
const FOOTER_H = 12

class Pdf {
    doc: jsPDF
    family: string
    pageWidth: number
    pageHeight: number
    y = PAGE_MARGIN
    maxY: number

    constructor(doc: jsPDF, family: string) {
        this.doc = doc
        this.family = family
        this.pageWidth = doc.internal.pageSize.getWidth()
        this.pageHeight = doc.internal.pageSize.getHeight()
        this.maxY = this.pageHeight - FOOTER_H
    }

    get right() { return this.pageWidth - PAGE_MARGIN }
    get contentWidth() { return this.pageWidth - PAGE_MARGIN * 2 }

    font(style: 'normal' | 'bold', size: number, color: RGB) {
        this.doc.setFont(this.family, style)
        this.doc.setFontSize(size)
        this.doc.setTextColor(color[0], color[1], color[2])
    }

    text(s: string, x: number, y: number, align: 'left' | 'right' | 'center' = 'left') {
        this.doc.text(s, x, y, { align })
    }

    width(s: string): number { return this.doc.getTextWidth(s) }

    // Recorta `s` con "…" para que quepa en `maxW` con la fuente activa.
    fit(s: string, maxW: number): string {
        if (this.width(s) <= maxW) return s
        let out = s
        while (out.length > 1 && this.width(out + '…') > maxW) out = out.slice(0, -1)
        return out.trimEnd() + '…'
    }

    rule(y: number, x1 = PAGE_MARGIN, x2 = this.right, color: RGB = C.rule, w = 0.2) {
        this.doc.setDrawColor(color[0], color[1], color[2])
        this.doc.setLineWidth(w)
        this.doc.line(x1, y, x2, y)
    }

    fill(x: number, y: number, w: number, h: number, color: RGB, r = 0) {
        this.doc.setFillColor(color[0], color[1], color[2])
        if (r > 0) this.doc.roundedRect(x, y, w, h, r, r, 'F')
        else this.doc.rect(x, y, w, h, 'F')
    }

    badge(label: string, x: number, baseline: number, colors: { fg: RGB; bg: RGB }, size = 6.5): number {
        this.font('normal', size, colors.fg)
        const w = this.width(label) + 3
        this.fill(x, baseline - 2.9, w, 3.9, colors.bg, 1)
        this.text(label, x + 1.5, baseline)
        return w
    }

    // Garantiza `h` mm disponibles; si no caben, abre página y corre `onNewPage`.
    ensure(h: number, onNewPage?: () => void) {
        if (this.y + h <= this.maxY) return
        this.doc.addPage()
        this.y = PAGE_MARGIN
        if (onNewPage) onNewPage()
    }
}

// Columnas de la tabla (bordes derechos para los montos)
const COL = {
    fecha: PAGE_MARGIN,
    salida: PAGE_MARGIN + 17,
    salidaW: 79,
    traspaso: PAGE_MARGIN + 118,
    entradas: PAGE_MARGIN + 140,
    abonado: PAGE_MARGIN + 162,
    pendiente: PAGE_MARGIN + 182
}
const GROUP_ROW_H = 5.6
const PAX_ROW_H = 4

function drawTableHeader(p: Pdf, cont = false) {
    p.font('normal', 6.5, C.muted)
    const y = p.y + 3.2
    p.text(cont ? 'FECHA (CONT.)' : 'FECHA', COL.fecha, y)
    p.text('SALIDA / PASAJERO', COL.salida, y)
    p.text('TRASPASO', COL.traspaso, y, 'right')
    p.text('ENTRADAS', COL.entradas, y, 'right')
    p.text('ABONADO', COL.abonado, y, 'right')
    p.text('PENDIENTE', COL.pendiente, y, 'right')
    p.rule(p.y + 4.6)
    p.y += 5.6
}

function ticketColors(status: string | null): { fg: RGB; bg: RGB } | null {
    if (!status) return null
    return TICKET_STATUS_COLORS[status] || TICKET_STATUS_OK
}

// Dibuja una salida (fila de grupo + líneas de pasajeros). `dim` = sección de saldados.
function drawGroup(p: Pdf, g: SalidaGroup, dim: boolean, onNewPage: () => void) {
    const tourLines: string[] = (() => {
        p.font('bold', 8, C.ink)
        const suffix = `  ·  ${g.pax.length} pax`
        const lines = p.doc.splitTextToSize(g.tour, COL.salidaW - p.width(suffix) - 1) as string[]
        lines[lines.length - 1] = lines[lines.length - 1] + suffix
        return lines
    })()
    const groupH = GROUP_ROW_H + (tourLines.length - 1) * 3.6
    const showPaxAmounts = g.pax.length > 1

    // Fila de grupo + al menos el primer pasajero deben ir juntos.
    p.ensure(groupH + PAX_ROW_H, onNewPage)

    const inkColor = dim ? C.muted : C.ink
    p.fill(PAGE_MARGIN, p.y, p.contentWidth, groupH, C.band)
    const base = p.y + 4
    p.font('bold', 8, inkColor)
    p.text(g.dateLabel, COL.fecha, base)
    tourLines.forEach((line, i) => {
        if (i === tourLines.length - 1) {
            const suffixIdx = line.lastIndexOf('  ·  ')
            const tourPart = line.slice(0, suffixIdx)
            const suffix = line.slice(suffixIdx)
            p.font('bold', 8, inkColor)
            p.text(tourPart, COL.salida, base + i * 3.6)
            const tourW = p.width(tourPart)
            p.font('normal', 7.5, C.muted)
            p.text(suffix, COL.salida + tourW, base + i * 3.6)
        } else {
            p.text(line, COL.salida, base + i * 3.6)
        }
    })
    p.font('bold', 8, inkColor)
    p.text(money(g.traspaso), COL.traspaso, base, 'right')
    p.text(money(g.entradas), COL.entradas, base, 'right')
    p.text(money(g.abonado), COL.abonado, base, 'right')
    if (g.pendiente > 0 && !dim) p.font('bold', 8, C.debtRed)
    p.text(g.pendiente === 0 ? '—' : money(g.pendiente), COL.pendiente, base, 'right')
    p.y += groupH

    g.pax.forEach((pax, idx) => {
        if (idx > 0) p.ensure(PAX_ROW_H, onNewPage)
        const b = p.y + 3
        const paxColor = pax.pendiente === 0 ? C.faint : C.body
        p.font('normal', 7.2, paxColor)
        let name = pax.name
        const colors = ticketColors(pax.ticket_status)
        const label = pax.ticket_status ? (TICKET_STATUS_LABELS[pax.ticket_status] || pax.ticket_status) : ''
        p.font('normal', 6.5, C.muted)
        const badgeW = label ? p.width(label) + 3 : 0
        const reviewedW = pax.reviewed ? p.width(REVIEWED_LABEL) + 3 : 0
        p.font('normal', 7.2, paxColor)
        name = p.fit(name, COL.salidaW - 2 - (badgeW ? badgeW + 2 : 0) - (reviewedW ? reviewedW + 1.5 : 0))
        p.text(name, COL.salida + 2, b)
        let badgeX = COL.salida + 2 + p.width(name) + 2
        if (colors && label) badgeX += p.badge(label, badgeX, b, colors) + 1.5
        if (pax.reviewed) p.badge(REVIEWED_LABEL, badgeX, b, REVIEWED_COLORS)
        if (showPaxAmounts) {
            p.font('normal', 6.8, paxColor)
            p.text(plain(pax.traspaso), COL.traspaso, b, 'right')
            p.text(plain(pax.entradas), COL.entradas, b, 'right')
            p.text(plain(pax.abonado), COL.abonado, b, 'right')
            p.text(pax.pendiente === 0 ? '—' : plain(pax.pendiente), COL.pendiente, b, 'right')
        }
        p.y += PAX_ROW_H
    })
    p.y += 1
}

function drawGeneralPaymentRow(p: Pdf, gp: GeneralPaymentRow, onNewPage: () => void) {
    p.ensure(GROUP_ROW_H, onNewPage)
    const base = p.y + 4.2
    p.font('normal', 8, C.body)
    p.text(gp.dateLabel, COL.fecha, base)
    p.font('bold', 8, C.body)
    const label = 'Abono general'
    p.text(label, COL.salida, base)
    const labelW = p.width(label)
    p.font('normal', 7.2, C.muted)
    const detail = [gp.method, gp.remarks].filter(Boolean).join(' · ')
    if (detail) p.text('  ' + p.fit(detail, COL.salidaW - labelW - 3), COL.salida + labelW, base)
    p.font('bold', 8, C.body)
    p.text(money(gp.amount), COL.abonado, base, 'right')
    p.font('normal', 8, C.creditGreen)
    p.text(money(-gp.amount), COL.pendiente, base, 'right')
    p.y += GROUP_ROW_H
}

function drawSectionTitle(p: Pdf, title: string, subtitle: string) {
    p.ensure(24)
    p.y += 2
    p.font('bold', 10, C.navy)
    p.text(title, PAGE_MARGIN, p.y + 4)
    p.font('normal', 8, C.muted)
    p.text(subtitle, p.right, p.y + 4, 'right')
    p.y += 6.5
    p.rule(p.y, PAGE_MARGIN, p.right, C.navy, 0.4)
    p.y += 1.5
}

function drawSide(p: Pdf, side: SideModel, title: string, subtitle: string) {
    drawSectionTitle(p, title, subtitle)
    const onNewPage = () => drawTableHeader(p, true)

    if (side.groups.length === 0 && side.generalPayments.length === 0) {
        p.font('normal', 8, C.muted)
        p.text(side.settled.length > 0 ? 'Sin saldo pendiente (todas las salidas están saldadas).' : 'Sin movimientos en este período.', PAGE_MARGIN, p.y + 4)
        p.y += 8
        return
    }

    drawTableHeader(p)
    side.groups.forEach(g => drawGroup(p, g, false, onNewPage))
    side.generalPayments.forEach(gp => drawGeneralPaymentRow(p, gp, onNewPage))

    // Total pendiente del lado = lo que aparece en la conciliación
    const pendingTotal = side.groups.reduce((a, g) => a + g.pendiente, 0) - side.generalPayments.reduce((a, g) => a + g.amount, 0)
    p.ensure(7)
    p.rule(p.y + 0.5)
    const base = p.y + 4.6
    p.font('bold', 8.5, C.ink)
    p.text('Total pendiente', COL.salida, base)
    p.font('bold', 8.5, pendingTotal > 0 ? C.debtRed : C.ink)
    p.text(money(pendingTotal), COL.pendiente, base, 'right')
    p.y += 6.5
}

// Saldados: una sola línea gris por salida (fecha, tour, quién la entregó, nombres) con lo abonado.
function drawSettled(p: Pdf, positive: SideModel, negative: SideModel, counterpart: string) {
    const items: { g: SalidaGroup; who: string }[] = [
        ...positive.settled.map(g => ({ g, who: `entregado por ${counterpart}` })),
        ...negative.settled.map(g => ({ g, who: `entregado por ${ISSUER.short}` }))
    ].sort((a, b) => a.g.date.localeCompare(b.g.date))
    if (items.length === 0) return

    drawSectionTitle(p, 'Saldados en el período', 'Salidas ya pagadas — no suman al saldo')
    const onNewPage = () => drawTableHeader(p, true)
    drawTableHeader(p)
    const ROW_H = 7.2
    items.forEach(({ g, who }) => {
        p.ensure(ROW_H, onNewPage)
        const base = p.y + 3.4
        p.font('normal', 7.5, C.muted)
        p.text(g.dateLabel, COL.fecha, base)
        p.text(p.fit(`${g.tour} · ${g.pax.length} pax`, COL.salidaW), COL.salida, base)
        p.text(plain(g.traspaso), COL.traspaso, base, 'right')
        p.text(plain(g.entradas), COL.entradas, base, 'right')
        p.text(plain(g.abonado), COL.abonado, base, 'right')
        p.text('—', COL.pendiente, base, 'right')
        p.font('normal', 6.8, C.faint)
        p.text(p.fit(`${g.pax.map(x => x.name).join(', ')} · ${who}`, COL.salidaW - 2), COL.salida + 2, base + 3.2)
        p.rule(p.y + ROW_H - 0.6, PAGE_MARGIN, p.right, C.band, 0.2)
        p.y += ROW_H
    })
    p.y += 2
}

// ─── Cabecera, resumen y recuadro de pendientes ──────────────────────────────

function drawHeader(p: Pdf, counterpart: string, periodLabel: string, generatedLabel: string) {
    try {
        p.doc.addImage('/assets/logo.png', 'PNG', PAGE_MARGIN, 9, 16, 16)
    } catch (error) {
        console.log('Could not load logo:', error)
    }
    p.font('bold', 12, C.navy)
    p.text(ISSUER.name, PAGE_MARGIN + 20, 15.5)
    p.font('normal', 8, C.navy)
    p.text(ISSUER.subtitle, PAGE_MARGIN + 20, 20)

    p.font('bold', 13, C.ink)
    p.text('Estado de cuenta', p.right, 14.5, 'right')
    p.font('normal', 10, C.body)
    p.text(counterpart, p.right, 20, 'right')
    p.font('normal', 7.5, C.muted)
    p.text(`${periodLabel}  ·  ${generatedLabel}`, p.right, 25, 'right')
    p.rule(29, PAGE_MARGIN, p.right, C.navy, 0.5)
    p.y = 34
}

interface Recon {
    counterpart: string
    positive: SideModel
    negative: SideModel
    opening: number | null
    openingLabel: string | null   // fecha del día anterior a `from`
    periodTotal: number
    finalTotal: number
    closingLabel: string
    saldoLabel: string            // "Saldo al dd/mm/aaaa" o "Saldo del período" (sin arrastre)
}

function whoOwes(total: number, counterpart: string): string {
    if (total > 0) return `${counterpart} debe a ${ISSUER.short}`
    if (total < 0) return `${ISSUER.short} debe a ${counterpart}`
    return 'Cuentas al día'
}

function drawSummary(p: Pdf, r: Recon) {
    const top = p.y
    // ── Izquierda: saldo grande ──
    p.font('normal', 7.5, C.muted)
    p.text(r.saldoLabel, PAGE_MARGIN, top + 4)
    const color = r.finalTotal > 0 ? C.debtRed : (r.finalTotal < 0 ? C.creditGreen : C.ink)
    p.font('bold', 18, color)
    p.text(money(Math.abs(r.finalTotal)), PAGE_MARGIN, top + 12)
    const leftW = 62
    p.font('bold', 8.5, color)
    p.text(p.fit(whoOwes(r.finalTotal, r.counterpart), leftW), PAGE_MARGIN, top + 17)
    if (r.opening !== null && r.openingLabel) {
        p.font('normal', 7.2, C.muted)
        p.text(p.fit(`Saldo anterior al ${r.openingLabel}: ${money(Math.abs(r.opening))}`, leftW), PAGE_MARGIN, top + 22)
        const tail = r.opening === 0 ? 'cuentas al día' : whoOwes(r.opening, r.counterpart)
        p.text(p.fit(`(${tail})`, leftW), PAGE_MARGIN, top + 25.5)
    } else if (r.openingLabel) {
        p.font('normal', 7.2, C.muted)
        p.text(p.fit('Sin saldo anterior (solo el período)', leftW), PAGE_MARGIN, top + 22)
    }

    // ── Derecha: conciliación por lado ──
    const x0 = PAGE_MARGIN + 66
    const cols = { label: x0, t: x0 + 62, e: x0 + 80, a: x0 + 98, r: p.right }
    let y = top + 4
    p.font('normal', 6.5, C.muted)
    p.text('TRASPASOS', cols.t, y, 'right')
    p.text('ENTRADAS', cols.e, y, 'right')
    p.text('ABONADO', cols.a, y, 'right')
    p.text('PENDIENTE', cols.r, y, 'right')
    p.rule(y + 1.4, x0, p.right)

    const labelW = cols.t - cols.label - 16
    const line = (label: string, t: SideModel['totals'], bold = false) => {
        y += 5
        p.font(bold ? 'bold' : 'normal', 8, C.body)
        p.text(p.fit(label, labelW), cols.label, y)
        p.font('normal', 8, C.body)
        p.text(plain(t.traspasos), cols.t, y, 'right')
        p.text('+ ' + plain(t.entradas), cols.e, y, 'right')
        p.text('− ' + plain(t.abonado), cols.a, y, 'right')
        p.font('bold', 8, t.pendiente > 0 ? C.ink : C.body)
        p.text('= ' + plain(t.pendiente), cols.r, y, 'right')
    }
    line(`${r.counterpart} debe a ${ISSUER.short}`, r.positive.totals)
    line(`${ISSUER.short} debe a ${r.counterpart}`, r.negative.totals)
    if (r.opening !== null) {
        y += 5
        p.font('normal', 8, C.body)
        p.text(p.fit(`Saldo anterior al ${r.openingLabel}`, labelW), cols.label, y)
        p.font('normal', 8, C.body)
        const signed = r.opening === 0 ? '0' : `${r.opening > 0 ? '+' : '−'} ${plain(Math.abs(r.opening))}`
        p.text(signed, cols.r, y, 'right')
    }
    y += 1.8
    p.rule(y, x0, p.right, C.body, 0.3)
    y += 4.2
    p.font('bold', 8.5, C.ink)
    p.text(r.saldoLabel, cols.label, y)
    p.font('bold', 8.5, color)
    const saldoTxt = r.finalTotal === 0 ? 'Cuentas al día' : `${whoOwes(r.finalTotal, r.counterpart)}   ${money(Math.abs(r.finalTotal))}`
    p.text(saldoTxt, cols.r, y, 'right')

    p.y = Math.max(top + 28, y + 4)
}

function drawPendingBox(p: Pdf, r: Recon) {
    const sides: { title: string; side: SideModel }[] = []
    if (r.positive.groups.length > 0 || r.positive.generalPayments.length > 0) {
        sides.push({ title: `${r.counterpart} debe a ${ISSUER.short}`, side: r.positive })
    }
    if (r.negative.groups.length > 0 || r.negative.generalPayments.length > 0) {
        sides.push({ title: `${ISSUER.short} debe a ${r.counterpart}`, side: r.negative })
    }
    if (sides.length === 0 && r.finalTotal === 0) return

    const pad = 4
    const innerW = p.contentWidth - pad * 2
    const colW = sides.length === 2 ? (innerW - 6) / 2 : innerW
    const lineH = 3.9

    // Medir. El recuadro es un resumen: como máximo MAX_LISTED salidas por lado, el resto se
    // resume en una línea ("… y N salidas más") — el detalle completo va en las tablas.
    const MAX_LISTED = 10
    const listed = (s: SideModel) => s.groups.length > MAX_LISTED ? MAX_LISTED + 1 : s.groups.length
    const linesPerSide = sides.map(s => listed(s.side) + (s.side.generalPayments.length > 0 ? 1 : 0) + 2)
    const bodyH = sides.length ? Math.max(...linesPerSide) * lineH : 0
    const showBank = r.finalTotal > 0
    const boxH = 9 + bodyH + (sides.length ? 1 : 0) + 6 + (showBank ? 12.5 : 0) + pad
    p.ensure(boxH)

    const top = p.y
    p.doc.setDrawColor(C.rule[0], C.rule[1], C.rule[2])
    p.doc.setLineWidth(0.3)
    p.doc.setFillColor(C.boxBg[0], C.boxBg[1], C.boxBg[2])
    p.doc.roundedRect(PAGE_MARGIN, top, p.contentWidth, boxH, 2, 2, 'FD')

    p.font('bold', 9, C.navy)
    p.text('Pendiente de pago', PAGE_MARGIN + pad, top + 6)
    let y = top + 11

    sides.forEach((s, i) => {
        const x = PAGE_MARGIN + pad + i * (colW + 6)
        const xr = x + colW
        let yy = y
        p.font('bold', 7.5, C.body)
        p.text(s.title, x, yy)
        yy += lineH
        const shown = s.side.groups.length > MAX_LISTED ? s.side.groups.slice(0, MAX_LISTED) : s.side.groups
        shown.forEach(g => {
            p.font('normal', 7.2, C.body)
            const amount = money(g.pendiente)
            const amountW = p.width(amount) + 2
            p.text(p.fit(`${g.dateLabel} · ${g.tour} · ${g.pax.length} pax`, colW - amountW - 2), x, yy)
            p.text(amount, xr, yy, 'right')
            yy += lineH
        })
        if (shown.length < s.side.groups.length) {
            const rest = s.side.groups.slice(MAX_LISTED)
            p.font('normal', 7.2, C.muted)
            p.text(`… y ${rest.length} salidas más (detalle abajo)`, x, yy)
            p.text(money(rest.reduce((a, g) => a + g.pendiente, 0)), xr, yy, 'right')
            yy += lineH
        }
        if (s.side.generalPayments.length > 0) {
            const sum = s.side.generalPayments.reduce((a, g) => a + g.amount, 0)
            p.font('normal', 7.2, C.creditGreen)
            p.text(`Abonos generales del período`, x, yy)
            p.text(money(-sum), xr, yy, 'right')
            yy += lineH
        }
        const subtotal = s.side.groups.reduce((a, g) => a + g.pendiente, 0) - s.side.generalPayments.reduce((a, g) => a + g.amount, 0)
        p.rule(yy - 2.6, x, xr)
        p.font('bold', 7.5, C.ink)
        p.text('Subtotal', x, yy)
        p.text(money(subtotal), xr, yy, 'right')
    })
    y += bodyH + (sides.length ? 1 : 0)

    // Neto
    p.rule(y - 1, PAGE_MARGIN + pad, p.right - pad, C.body, 0.3)
    y += 3.5
    const netColor = r.finalTotal > 0 ? C.debtRed : (r.finalTotal < 0 ? C.creditGreen : C.ink)
    p.font('bold', 9, netColor)
    const netLabel = r.finalTotal > 0
        ? `Neto a pagar por ${r.counterpart}:  ${money(r.finalTotal)}`
        : r.finalTotal < 0
            ? `Neto a pagar por ${ISSUER.short}:  ${money(-r.finalTotal)}`
            : 'Neto: cuentas al día (los pendientes se compensan)'
    p.text(netLabel, PAGE_MARGIN + pad, y)
    if (r.opening !== null && r.opening !== 0) {
        p.font('normal', 6.8, C.muted)
        p.text(`incluye saldo anterior al ${r.openingLabel}`, p.right - pad, y, 'right')
    }

    if (showBank) {
        y += 5
        p.font('normal', 7, C.body)
        const b = ISSUER.bank
        p.text(`Transferir a: ${b.holder} · RUT ${b.rut} · ${b.bank} · ${b.account} · ${b.email}`, PAGE_MARGIN + pad, y)
        y += 3.6
        p.font('normal', 6.8, C.muted)
        p.text(`Enviar el comprobante a ${ISSUER.contact_email}.`, PAGE_MARGIN + pad, y)
        y += 3.6
        // Línea en portugués: varias contrapartes pagan desde un sector financiero brasileño.
        p.text(p.fit(`PT · Saldo a pagar pela ${r.counterpart}: ${money(r.finalTotal)} CLP · Dúvidas: ${ISSUER.contact_email}`, p.contentWidth - pad * 2), PAGE_MARGIN + pad, y)
    }

    p.y = top + boxH + 5
}

// ─── Construcción del documento ──────────────────────────────────────────────

export function buildAccountStatementPdf(statement: AccountStatement, today: Date = new Date()): jsPDF {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })

    // jsPDF no lanza si el .ttf no se pudo cargar (lo loguea vía PubSub), así que además
    // del try/catch se verifica que la familia quedó registrada antes de usarla.
    let family = 'Roboto'
    try {
        doc.addFont('/fonts/Roboto-Regular.ttf', 'Roboto', 'normal')
        doc.addFont('/fonts/Roboto-Bold.ttf', 'Roboto', 'bold')
        const registered = doc.getFontList()['Roboto'] || []
        if (!registered.includes('normal') || !registered.includes('bold')) throw new Error('Roboto not registered')
    } catch (error) {
        console.warn('Font loading failed, using default fonts:', error)
        family = 'helvetica'
    }
    const p = new Pdf(doc, family)

    const counterpart = statement.counterpart_agency?.display_name || statement.counterpart_agency?.name || 'Agencia'
    const todayLabel = fmtToday(today)
    const closingLabel = statement.to ? fmtDate(statement.to) : todayLabel
    const periodLabel = statement.from
        ? `Período ${fmtDate(statement.from)} – ${closingLabel}`
        : `Histórico completo al ${closingLabel}`

    const positive = buildSide(statement.positive || [])
    const negative = buildSide(statement.negative || [])
    const periodTotal = Number(statement.summary?.total) || 0
    const opening = (statement.from && statement.opening_balance !== undefined && statement.opening_balance !== null)
        ? Number(statement.opening_balance) || 0
        : null
    // "Saldo anterior al <from>" = todo lo acumulado antes de esa fecha (el backend lo calcula hasta from − 1 día).
    const openingLabel = statement.from ? fmtDate(statement.from) : null

    const recon: Recon = {
        counterpart, positive, negative,
        opening, openingLabel,
        periodTotal,
        finalTotal: periodTotal + (opening || 0),
        closingLabel,
        saldoLabel: (statement.from && opening === null) ? 'Saldo del período' : `Saldo al ${closingLabel}`
    }

    drawHeader(p, counterpart, periodLabel, `Generado el ${todayLabel}`)
    drawSummary(p, recon)
    drawPendingBox(p, recon)

    const noMovements = statement.positive.length === 0 && statement.negative.length === 0
    if (noMovements) {
        p.font('normal', 9, C.muted)
        p.text('No hay movimientos registrados en este período.', p.pageWidth / 2, p.y + 10, 'center')
    } else {
        drawSide(p, positive, `Pasajeros que ${counterpart} entregó a ${ISSUER.short}`, `${counterpart} debe a ${ISSUER.short}`)
        p.y += 2
        drawSide(p, negative, `Pasajeros que ${ISSUER.short} entregó a ${counterpart}`, `${ISSUER.short} debe a ${counterpart}`)
        p.y += 2
        drawSettled(p, positive, negative, counterpart)
    }

    // Pie de página
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        p.rule(p.pageHeight - 9, PAGE_MARGIN, p.right)
        p.font('normal', 6.8, C.faint)
        p.text(`${ISSUER.name} · Estado de cuenta ${counterpart} · ${periodLabel}`, PAGE_MARGIN, p.pageHeight - 5.5)
        p.text(`Generado por Traspax · página ${i} de ${pageCount}`, p.right, p.pageHeight - 5.5, 'right')
    }

    return doc
}

// ─── Entry point (descarga en el navegador) ──────────────────────────────────

export default async function generateAccountStatementPdf(statement: AccountStatement) {
    const doc = buildAccountStatementPdf(statement)
    const counterpart = statement.counterpart_agency?.display_name || statement.counterpart_agency?.name || 'Agencia'
    const fileSafeName = counterpart.toString().replace(/[^a-z0-9]+/gi, '_')
    doc.save(`estado_cuenta_${fileSafeName}.pdf`)
}
